import { useEffect, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import "../CommonDesign.scss";
import editIcon from "../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../assets/imgs/master/delete_icon_wh.svg";

interface BaseStopProps {
  baseId: number | null;
}

interface EditableBaseStop {
  base_stop_id: number | null;
  base_id: number;
  base_stop_name: string;
  map_latitude: number;
  map_longitude: number;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
}

const BaseStop = ({ baseId }: BaseStopProps) => {
  const [rows, setRows] = useState<EditableBaseStop[]>([]);
  const [originalRows, setOriginalRows] = useState<EditableBaseStop[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!baseId) return;

    const fetchData = async () => {
      const res = await operatorApiService.doGetBaseStops({ baseId });
      const mapped = res.map((entry: any) => ({
        ...entry,
        status: "original",
        isEditing: false,
        internalId: `existing-${entry.base_stop_id}`,
      }));
      setRows(mapped);
      setOriginalRows(mapped);
    };

    fetchData();
  }, [baseId]);

  const handleChange = (
    index: number,
    field: keyof EditableBaseStop,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        base_stop_id: null,
        base_id: baseId!,
        base_stop_name: "",
        map_latitude: 0,
        map_longitude: 0,
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}`,
      },
    ]);
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? { ...r, isEditing: true, status: "edited" }
          : r
      )
    );
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const handleReset = () => {
    setRows(originalRows.map((r) => ({ ...r })));
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.base_stop_name.trim() === "" ||
            r.map_latitude === null ||
            r.map_longitude === null)
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          r.base_stop_name.trim().length > 25
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows
        .filter((r) => r.status === "new")
        .map(({ base_stop_name, map_latitude, map_longitude }) => ({
          base_id: baseId!,
          base_stop_name,
          map_latitude,
          map_longitude,
        }));

      const update = rows
        .filter((r) => r.status === "edited")
        .map(
          ({ base_stop_id, base_stop_name, map_latitude, map_longitude }) => ({
            base_stop_id,
            base_stop_name,
            map_latitude,
            map_longitude,
          })
        );

      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.base_stop_id);

      const response = await operatorApiService.saveBaseStops({
        baseId,
        create,
        update,
        delete: del,
      });

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);

        const refreshed = await operatorApiService.doGetBaseStops({ baseId });
        const mapped = refreshed.map((entry: any) => ({
          ...entry,
          status: "original",
          isEditing: false,
          internalId: `existing-${entry.base_stop_id}`,
        }));
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。\n" + error);
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  return (
    <div className="vehicleClass">
      <div className="content">
        <div className="title">停留所</div>
        <div className="section">
          <div className="sectionTitle">停留所一覧</div>
          <div className="sectionBody">
            <table className="tableArea">
              <thead>
                <tr>
                  <th>停留所ID</th>
                  <th>停留所名</th>
                  <th>緯度</th>
                  <th>経度</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.internalId}>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.base_stop_id ?? "新規"}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.base_stop_name}
                          onChange={(e) =>
                            handleChange(i, "base_stop_name", e.target.value)
                          }
                        />
                      ) : (
                        row.base_stop_name
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          type="number"
                          value={row.map_latitude}
                          onChange={(e) =>
                            handleChange(i, "map_latitude", e.target.value)
                          }
                        />
                      ) : (
                        row.map_latitude
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          type="number"
                          value={row.map_longitude}
                          onChange={(e) =>
                            handleChange(i, "map_longitude", e.target.value)
                          }
                        />
                      ) : (
                        row.map_longitude
                      )}
                    </td>
                    <td>
                      {row.status === "deleted" ? (
                        <span className="deletionLabel">削除予定</span>
                      ) : row.status === "edited" ? (
                        <span className="editLabel">編集予定</span>
                      ) : row.status === "new" ? (
                        <button
                          className="cancelButton"
                          onClick={() => markDeleted(row.internalId)}
                        >
                          取消
                        </button>
                      ) : (
                        <div className="buttonGroup">
                          <button
                            className="updateButton"
                            onClick={() => toggleEdit(row.internalId)}
                          >
                            <img src={editIcon} alt="" className="icon" />
                            編集
                          </button>
                          <button
                            className="deleteButton"
                            onClick={() => markDeleted(row.internalId)}
                          >
                            <img src={deleteIcon} alt="" className="icon" />
                            削除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="createFooter">
              <div className="createLabel">
                表に新規入力欄が追加されます。
                新規停留所を入力し、登録してください。
              </div>
              <button className="createButton" onClick={addRow}>
                新規行追加
              </button>
            </div>
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
            {rows.some((r) => r.status !== "original") && (
              <div className="actionButtons">
                <button className="backButton" onClick={handleReset}>
                  戻る
                </button>
                <button className="decisionButton" onClick={submit}>
                  確定
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseStop;
